<template>
  <div class="gl-page">
    <!-- Page Header -->
    <div class="gl-page-header">
      <div class="gl-page-header-left">
        <router-link to="/super-admin/promos" class="gl-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Promos
        </router-link>
        <h1 class="gl-page-title">{{ isEdit ? 'Edit Promo Code' : 'Create Promo Code' }}</h1>
        <p class="gl-page-sub">{{ isEdit ? 'Update the promo code details' : 'Set up a new discount code for customers' }}</p>
      </div>
      <div v-if="!isEdit" class="gl-page-header-right">
        <div class="gl-quick-fill-wrap">
          <button class="gl-btn-ghost" @click="showQuickFill = !showQuickFill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            Quick Fill
          </button>
          <div v-if="showQuickFill" class="gl-quick-fill-menu">
            <button class="gl-quick-fill-item" @click="prefillPercentage(); showQuickFill=false">10% Off (Percentage)</button>
            <button class="gl-quick-fill-item" @click="prefillFixed(); showQuickFill=false">RM5 Off (Fixed)</button>
            <button class="gl-quick-fill-item" @click="prefillWelcome(); showQuickFill=false">Welcome Promo</button>
            <div class="gl-quick-fill-divider"></div>
            <button class="gl-quick-fill-item gl-quick-fill-item-red" @click="clearForm(); showQuickFill=false">Clear Form</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="gl-form-skel-wrap">
      <div class="gl-form-skel-card">
        <div v-for="i in 8" :key="i" class="gl-form-skel-field">
          <div class="gl-skel" style="width:80px;height:10px;margin-bottom:8px"></div>
          <div class="gl-skel" style="width:100%;height:38px;border-radius:9px"></div>
        </div>
      </div>
    </div>

    <div v-else class="gl-form-layout">
      <!-- Main Form Column -->
      <div class="gl-form-main">
        <form @submit.prevent="submitForm">

          <!-- Basic Information -->
          <div class="gl-form-card">
            <div class="gl-form-section-title">Basic Information</div>

            <div class="gl-form-row-2">
              <div class="gl-form-field">
                <label class="gl-form-label">Promo Code <span class="gl-required">*</span></label>
                <div class="gl-input-wrap">
                  <input
                    type="text"
                    class="gl-input gl-input-mono"
                    :class="{ 'gl-input-error': errors.code, 'gl-input-ok': codeAvailable === true }"
                    v-model="form.code"
                    placeholder="e.g. SAVE10, WELCOME20"
                    style="text-transform:uppercase"
                    @input="checkCodeAvailability"
                    required
                  >
                  <div v-if="checkingCode" class="gl-input-spinner"><span class="gl-spin-dark"></span></div>
                </div>
                <div v-if="errors.code" class="gl-field-error">{{ errors.code[0] }}</div>
                <div v-else-if="codeAvailable === true" class="gl-field-ok">Code is available!</div>
                <div v-else-if="codeAvailable === false" class="gl-field-error">This code is already in use.</div>
              </div>

              <div class="gl-form-field">
                <label class="gl-form-label">Display Name <span class="gl-required">*</span></label>
                <input
                  type="text"
                  class="gl-input"
                  :class="{ 'gl-input-error': errors.name }"
                  v-model="form.name"
                  placeholder="e.g. 10% Off First Order"
                  required
                >
                <div v-if="errors.name" class="gl-field-error">{{ errors.name[0] }}</div>
              </div>
            </div>

            <div class="gl-form-field">
              <label class="gl-form-label">Description <span class="gl-form-label-opt">(optional)</span></label>
              <textarea class="gl-input gl-textarea" v-model="form.description" rows="2" placeholder="Internal reference note"></textarea>
            </div>
          </div>

          <!-- Discount Settings -->
          <div class="gl-form-card">
            <div class="gl-form-section-title">Discount Settings</div>

            <div class="gl-form-row-3">
              <div class="gl-form-field">
                <label class="gl-form-label">Discount Type <span class="gl-required">*</span></label>
                <select class="gl-input" v-model="form.discount_type" :class="{ 'gl-input-error': errors.discount_type }">
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (RM)</option>
                </select>
                <div v-if="errors.discount_type" class="gl-field-error">{{ errors.discount_type[0] }}</div>
              </div>

              <div class="gl-form-field">
                <label class="gl-form-label">Discount Value <span class="gl-required">*</span></label>
                <div class="gl-input-addon-wrap">
                  <input
                    type="number"
                    class="gl-input"
                    :class="{ 'gl-input-error': errors.discount_value }"
                    v-model="form.discount_value"
                    :max="form.discount_type === 'percentage' ? 100 : undefined"
                    min="0.01"
                    step="0.01"
                    required
                  >
                  <span class="gl-input-addon">{{ form.discount_type === 'percentage' ? '%' : 'RM' }}</span>
                </div>
                <div v-if="errors.discount_value" class="gl-field-error">{{ errors.discount_value[0] }}</div>
              </div>

              <div class="gl-form-field" v-if="form.discount_type === 'percentage'">
                <label class="gl-form-label">Max Discount Cap <span class="gl-form-label-opt">(RM)</span></label>
                <div class="gl-input-addon-wrap">
                  <span class="gl-input-addon gl-input-addon-left">RM</span>
                  <input type="number" class="gl-input gl-input-has-left-addon" v-model="form.max_discount_amount" min="0" step="0.01" placeholder="No cap">
                </div>
              </div>
            </div>

            <div class="gl-form-row-2">
              <div class="gl-form-field">
                <label class="gl-form-label">Minimum Order Amount <span class="gl-form-label-opt">(RM)</span></label>
                <div class="gl-input-addon-wrap">
                  <span class="gl-input-addon gl-input-addon-left">RM</span>
                  <input type="number" class="gl-input gl-input-has-left-addon" v-model="form.min_order_amount" min="0" step="0.01" placeholder="No minimum">
                </div>
              </div>
            </div>
          </div>

          <!-- Usage Limits -->
          <div class="gl-form-card">
            <div class="gl-form-section-title">Usage Limits</div>

            <div class="gl-form-row-2">
              <div class="gl-form-field">
                <label class="gl-form-label">Total Usage Limit</label>
                <input type="number" class="gl-input" v-model="form.usage_limit" min="1" placeholder="Unlimited">
                <div class="gl-field-hint">Leave empty for unlimited uses</div>
              </div>
              <div class="gl-form-field">
                <label class="gl-form-label">Limit Per Customer</label>
                <input type="number" class="gl-input" v-model="form.usage_limit_per_user" min="1" placeholder="Unlimited">
                <div class="gl-field-hint">Leave empty for no per-user limit</div>
              </div>
            </div>
          </div>

          <!-- Validity Period -->
          <div class="gl-form-card">
            <div class="gl-form-section-title">Validity Period</div>

            <div class="gl-form-row-2">
              <div class="gl-form-field">
                <label class="gl-form-label">Start Date <span class="gl-form-label-opt">(optional)</span></label>
                <input type="datetime-local" class="gl-input" v-model="form.starts_at">
              </div>
              <div class="gl-form-field">
                <label class="gl-form-label">Expiry Date <span class="gl-form-label-opt">(optional)</span></label>
                <input type="datetime-local" class="gl-input" :class="{ 'gl-input-error': errors.expires_at }" v-model="form.expires_at">
                <div v-if="errors.expires_at" class="gl-field-error">{{ errors.expires_at[0] }}</div>
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="gl-form-card">
            <div class="gl-status-row">
              <div>
                <div class="gl-form-section-title" style="margin-bottom:2px">Active Status</div>
                <div class="gl-field-hint">When enabled, customers can use this promo code</div>
              </div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': form.is_active }" @click="form.is_active = !form.is_active">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="gl-form-actions">
            <router-link to="/super-admin/promos" class="gl-btn-ghost">Cancel</router-link>
            <button type="submit" class="gl-btn-primary" :disabled="submitting || codeAvailable === false">
              <span v-if="submitting" class="gl-spin"></span>
              {{ isEdit ? 'Update Promo Code' : 'Create Promo Code' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Sidebar Column -->
      <div class="gl-form-sidebar">

        <!-- Live Preview -->
        <div class="gl-sidebar-card">
          <div class="gl-sidebar-card-title">Live Preview</div>
          <div class="gl-preview-box">
            <div class="gl-preview-top">
              <div class="gl-preview-code">{{ form.code || 'PROMO' }}</div>
              <span class="gl-badge" :class="form.is_active ? 'gl-badge-green' : 'gl-badge-gray'">
                {{ form.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="gl-preview-name">{{ form.name || 'Promo Name' }}</div>
            <div class="gl-preview-desc">{{ form.description || 'No description' }}</div>
            <div class="gl-preview-discount">
              <template v-if="form.discount_type === 'percentage'">
                {{ form.discount_value || 0 }}% OFF
              </template>
              <template v-else>
                RM{{ parseFloat(form.discount_value || 0).toFixed(2) }} OFF
              </template>
            </div>
          </div>

          <div class="gl-preview-details">
            <div v-if="form.min_order_amount" class="gl-preview-row">
              <span>Min. order</span>
              <span>RM{{ parseFloat(form.min_order_amount).toFixed(2) }}</span>
            </div>
            <div v-if="form.max_discount_amount && form.discount_type === 'percentage'" class="gl-preview-row">
              <span>Max. discount</span>
              <span>RM{{ parseFloat(form.max_discount_amount).toFixed(2) }}</span>
            </div>
            <div v-if="form.usage_limit" class="gl-preview-row">
              <span>Total uses</span>
              <span>{{ form.usage_limit }}</span>
            </div>
            <div v-if="form.usage_limit_per_user" class="gl-preview-row">
              <span>Per customer</span>
              <span>{{ form.usage_limit_per_user }}x</span>
            </div>
            <div v-if="form.starts_at" class="gl-preview-row">
              <span>Starts</span>
              <span>{{ formatDateTime(form.starts_at) }}</span>
            </div>
            <div v-if="form.expires_at" class="gl-preview-row">
              <span>Expires</span>
              <span>{{ formatDateTime(form.expires_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Usage Stats (Edit mode) -->
        <div v-if="isEdit && usageStats" class="gl-sidebar-card">
          <div class="gl-sidebar-card-title">Usage Statistics</div>
          <div class="gl-stats-2x2">
            <div class="gl-mini-stat">
              <div class="gl-mini-stat-val">{{ usageStats.times_used }}</div>
              <div class="gl-mini-stat-label">Total Uses</div>
            </div>
            <div class="gl-mini-stat">
              <div class="gl-mini-stat-val">RM{{ parseFloat(usageStats.total_discount_given || 0).toFixed(2) }}</div>
              <div class="gl-mini-stat-label">Total Discount</div>
            </div>
            <div class="gl-mini-stat">
              <div class="gl-mini-stat-val">{{ usageStats.unique_users }}</div>
              <div class="gl-mini-stat-label">Unique Users</div>
            </div>
            <div class="gl-mini-stat">
              <div class="gl-mini-stat-val">RM{{ parseFloat(usageStats.average_discount || 0).toFixed(2) }}</div>
              <div class="gl-mini-stat-label">Avg. Discount</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminPromoApi } from '@/services/superAdminApi';

export default {
  name: 'SAPromoForm',
  data() {
    return {
      loading: false,
      submitting: false,
      checkingCode: false,
      codeAvailable: null,
      isEdit: false,
      usageStats: null,
      errors: {},
      showQuickFill: false,
      form: {
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_amount: '',
        max_discount_amount: '',
        usage_limit: '',
        usage_limit_per_user: '',
        starts_at: '',
        expires_at: '',
        is_active: true
      },
      codeCheckTimer: null
    };
  },
  mounted() {
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchPromo();
    }
    document.addEventListener('click', this.closeQuickFill);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeQuickFill);
  },
  methods: {
    closeQuickFill(e) {
      if (!this.$el.querySelector('.gl-quick-fill-wrap')?.contains(e.target)) {
        this.showQuickFill = false;
      }
    },
    async fetchPromo() {
      try {
        this.loading = true;
        const response = await superAdminPromoApi.get(this.$route.params.id);
        const promo = response.data.data;
        this.form = {
          code: promo.code,
          name: promo.name,
          description: promo.description || '',
          discount_type: promo.discount_type,
          discount_value: promo.discount_value,
          min_order_amount: promo.min_order_amount || '',
          max_discount_amount: promo.max_discount_amount || '',
          usage_limit: promo.usage_limit || '',
          usage_limit_per_user: promo.usage_limit_per_user || '',
          starts_at: this.formatDateTimeForInput(promo.starts_at),
          expires_at: this.formatDateTimeForInput(promo.expires_at),
          is_active: promo.is_active
        };
        this.usageStats = promo.usage_stats;
      } catch (e) {
        console.error(e);
        this.$router.push('/super-admin/promos');
      } finally { this.loading = false; }
    },
    formatDateTimeForInput(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toISOString().slice(0, 16);
    },
    formatDateTime(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    checkCodeAvailability() {
      clearTimeout(this.codeCheckTimer);
      this.codeAvailable = null;
      const code = this.form.code.trim();
      if (!code || code.length < 2) return;
      this.codeCheckTimer = setTimeout(async () => {
        try {
          this.checkingCode = true;
          const excludeId = this.isEdit ? this.$route.params.id : null;
          const response = await superAdminPromoApi.checkCode(code, excludeId);
          this.codeAvailable = response.data.data.available;
        } catch (e) { console.error(e); }
        finally { this.checkingCode = false; }
      }, 500);
    },
    async submitForm() {
      try {
        this.submitting = true;
        this.errors = {};
        const data = { ...this.form };
        ['min_order_amount', 'max_discount_amount', 'usage_limit', 'usage_limit_per_user', 'starts_at', 'expires_at'].forEach(f => {
          if (data[f] === '') data[f] = null;
        });
        if (this.isEdit) {
          await superAdminPromoApi.update(this.$route.params.id, data);
        } else {
          await superAdminPromoApi.create(data);
        }
        this.$router.push('/super-admin/promos');
      } catch (e) {
        console.error(e);
        if (e.response?.data?.errors) this.errors = e.response.data.errors;
        else alert(e.response?.data?.message || 'Failed to save promo code');
      } finally { this.submitting = false; }
    },
    generateRandomCode(prefix = 'TEST') {
      return `${prefix}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    },
    prefillPercentage() {
      this.form = { code: this.generateRandomCode('SAVE'), name: '10% Off Your Order', description: 'Get 10% discount on your order', discount_type: 'percentage', discount_value: 10, min_order_amount: 20, max_discount_amount: 15, usage_limit: 100, usage_limit_per_user: 1, starts_at: '', expires_at: '', is_active: true };
      this.codeAvailable = null; this.checkCodeAvailability();
    },
    prefillFixed() {
      this.form = { code: this.generateRandomCode('RM5'), name: 'RM5 Off', description: 'Get RM5 off your order', discount_type: 'fixed', discount_value: 5, min_order_amount: 15, max_discount_amount: '', usage_limit: 50, usage_limit_per_user: 2, starts_at: '', expires_at: '', is_active: true };
      this.codeAvailable = null; this.checkCodeAvailability();
    },
    prefillWelcome() {
      const expiry = new Date(); expiry.setDate(expiry.getDate() + 30);
      this.form = { code: this.generateRandomCode('WELCOME'), name: 'Welcome Discount', description: 'Special discount for new customers', discount_type: 'percentage', discount_value: 15, min_order_amount: 25, max_discount_amount: 20, usage_limit: '', usage_limit_per_user: 1, starts_at: '', expires_at: expiry.toISOString().slice(0, 16), is_active: true };
      this.codeAvailable = null; this.checkCodeAvailability();
    },
    clearForm() {
      this.form = { code: '', name: '', description: '', discount_type: 'percentage', discount_value: '', min_order_amount: '', max_discount_amount: '', usage_limit: '', usage_limit_per_user: '', starts_at: '', expires_at: '', is_active: true };
      this.codeAvailable = null; this.errors = {};
    }
  }
};
</script>

<style>
/* ── Page shell ── */
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }
.gl-back-btn { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #6b7f7b; text-decoration: none; margin-bottom: 8px; font-weight: 500; }
.gl-back-btn:hover { color: #900C27; }

/* ── Buttons ── */
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 10px 20px; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none; transition: background .15s; }
.gl-btn-primary:hover { background: #1a5048; }
.gl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 10px 20px; font-size: 14px; font-weight: 500; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }

/* ── Quick Fill ── */
.gl-quick-fill-wrap { position: relative; }
.gl-quick-fill-menu { position: absolute; right: 0; top: calc(100% + 6px); background: #fff; border: 1px solid #eaedec; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,.12); padding: 6px; min-width: 200px; z-index: 100; }
.gl-quick-fill-item { display: block; width: 100%; text-align: left; padding: 9px 14px; font-size: 13px; color: #374151; background: transparent; border: none; border-radius: 8px; cursor: pointer; }
.gl-quick-fill-item:hover { background: #f8faf9; }
.gl-quick-fill-item-red { color: #dc2626; }
.gl-quick-fill-item-red:hover { background: #fef2f2; }
.gl-quick-fill-divider { height: 1px; background: #f0f3f2; margin: 4px 0; }

/* ── Form layout ── */
.gl-form-layout { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: start; }
.gl-form-main { display: flex; flex-direction: column; gap: 28px; }
.gl-form-main form { display: flex; flex-direction: column; gap: 28px; }
.gl-form-sidebar { display: flex; flex-direction: column; gap: 16px; }

/* ── Form cards ── */
.gl-form-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 22px 24px; }
.gl-form-section-title { font-size: 12px; font-weight: 700; color: #6b7f7b; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 18px; }
.gl-form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-form-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
.gl-form-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.gl-form-field:last-child { margin-bottom: 0; }
.gl-form-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-form-label-opt { font-weight: 400; color: #9ca3af; font-size: 11.5px; }
.gl-required { color: #dc2626; }
.gl-field-hint { font-size: 11.5px; color: #9ca3af; }
.gl-field-error { font-size: 12px; color: #dc2626; }
.gl-field-ok { font-size: 12px; color: #16a34a; }

/* ── Inputs ── */
.gl-input { width: 100%; padding: 9px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; transition: border-color .15s; appearance: auto; }
.gl-input:focus { border-color: #900C27; }
.gl-input-error { border-color: #dc2626 !important; }
.gl-input-ok { border-color: #16a34a !important; }
.gl-input-mono { font-family: 'SF Mono', 'Fira Code', monospace; font-weight: 700; letter-spacing: .5px; }
.gl-textarea { resize: vertical; min-height: 64px; }
.gl-input-wrap { position: relative; }
.gl-input-spinner { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); }
.gl-input-addon-wrap { position: relative; display: flex; align-items: center; }
.gl-input-addon-wrap .gl-input { padding-right: 36px; }
.gl-input-addon { position: absolute; right: 10px; font-size: 12.5px; color: #9ca3af; font-weight: 600; pointer-events: none; }
.gl-input-addon-left { right: auto; left: 10px; }
.gl-input-addon-wrap .gl-input-has-left-addon { padding-left: 34px; }

/* ── Spinners ── */
.gl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
.gl-spin-dark { display: inline-block; width: 13px; height: 13px; border: 2px solid #e0e7e5; border-top-color: #900C27; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }

/* ── Status toggle ── */
.gl-status-row { display: flex; align-items: center; justify-content: space-between; }
.gl-toggle { width: 44px; height: 24px; border-radius: 12px; background: #e0e7e5; border: none; cursor: pointer; position: relative; transition: background .2s; flex-shrink: 0; padding: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; display: block; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }

/* ── Form actions ── */
.gl-form-actions { display: flex; justify-content: flex-end; gap: 10px; padding: 4px 0; }

/* ── Form skeleton ── */
.gl-form-skel-wrap { display: grid; grid-template-columns: 1fr 320px; gap: 20px; }
.gl-form-skel-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 24px; display: flex; flex-direction: column; gap: 16px; }
.gl-form-skel-field { display: flex; flex-direction: column; gap: 8px; }

/* ── Sidebar cards ── */
.gl-sidebar-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 20px; }
.gl-sidebar-card-title { font-size: 12px; font-weight: 700; color: #6b7f7b; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 16px; }

/* ── Preview ── */
.gl-preview-box { background: #f8faf9; border: 1px solid #eaedec; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.gl-preview-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.gl-preview-code { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 14px; font-weight: 800; color: #900C27; letter-spacing: .5px; }
.gl-preview-name { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 2px; }
.gl-preview-desc { font-size: 12px; color: #9ca3af; margin-bottom: 12px; }
.gl-preview-discount { font-size: 22px; font-weight: 800; color: #16a34a; text-align: center; padding: 10px; background: #fff; border-radius: 9px; border: 1px solid #e0e7e5; }
.gl-preview-details { display: flex; flex-direction: column; gap: 8px; }
.gl-preview-row { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; }
.gl-preview-row span:first-child { color: #9ca3af; }
.gl-preview-row span:last-child { color: #374151; font-weight: 600; }

/* ── Mini stats 2x2 ── */
.gl-stats-2x2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.gl-mini-stat { background: #f8faf9; border-radius: 10px; padding: 12px 14px; }
.gl-mini-stat-val { font-size: 17px; font-weight: 700; color: #111827; line-height: 1; margin-bottom: 4px; }
.gl-mini-stat-label { font-size: 11px; color: #9ca3af; }

/* ── Badges ── */
.gl-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; }
.gl-badge-green { background: #dcfce7; color: #15803d; }
.gl-badge-gray { background: #f1f5f9; color: #64748b; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .gl-form-layout { grid-template-columns: 1fr; }
  .gl-form-skel-wrap { grid-template-columns: 1fr; }
  .gl-form-sidebar { order: -1; }
  .gl-sidebar-card { display: none; }
  .gl-sidebar-card:last-child { display: block; }
}

@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-form-row-2 { grid-template-columns: 1fr; gap: 0; }
  .gl-form-row-3 { grid-template-columns: 1fr; gap: 0; }
  .gl-form-card { padding: 16px; }
  .gl-form-actions { flex-direction: column-reverse; }
  .gl-form-actions .gl-btn-primary,
  .gl-form-actions .gl-btn-ghost { width: 100%; justify-content: center; }
  .gl-quick-fill-menu { right: 0; left: auto; }
  .gl-sidebar-card { display: block; }
}

/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub,
body.dark-only .gl-back-btn { color: #94a3b8; }
body.dark-only .gl-back-btn:hover { color: #4ade80; }
body.dark-only .gl-form-card,
body.dark-only .gl-sidebar-card,
body.dark-only .gl-quick-fill-menu { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-form-label { color: #e2e8f0; }
body.dark-only .gl-input { background: #162220; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-input:focus { border-color: #900C27; }
body.dark-only .gl-toggle { background: #2d3f3b; }
body.dark-only .gl-preview-box { background: #162220; border-color: #2d3f3b; }
body.dark-only .gl-preview-name { color: #f1f5f9; }
body.dark-only .gl-mini-stat { background: #162220; }
body.dark-only .gl-mini-stat-val { color: #f1f5f9; }
body.dark-only .gl-preview-discount { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-quick-fill-item { color: #e2e8f0; }
body.dark-only .gl-quick-fill-item:hover { background: #162220; }
body.dark-only .gl-quick-fill-divider { background: #2d3f3b; }
</style>
